import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchNationalities,
  fetchCountries,
  fetchReceivers,
  fetchInterPayments,
} from "../actioncreators/data";
import {
  fetchInterPaymentForEdit,
  updateInterPayment,
} from "redux/actioncreators/dataEditPayments";
type AnyObj = Record<string, any>;
export interface InterPaymentItem extends AnyObj {}
export interface InterPaymentsMeta {
  list: InterPaymentItem[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}
interface ReceiverState {
  nationalities: any[];
  countries: any[];
  receivers: any[];
  interPayments: InterPaymentsMeta; // <-- объект с пагинацией
  interPaymentForEdit: { data: InterPaymentItem } | null;
  selectedReceiver: any | null;
  isLoading: boolean;
}
const initialState: ReceiverState = {
  nationalities: [],
  countries: [],
  receivers: [],
  interPayments: {
    list: [],
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
  },
  interPaymentForEdit: null,
  selectedReceiver: null,
  isLoading: false,
};
export const receiverSlice = createSlice({
  name: "receiver",
  initialState,
  reducers: {
    setSelectedReceiver: (state, action: PayloadAction<any>) => {
      state.selectedReceiver = action.payload;
    },
    clearSelectedReceiver: (state) => {
      state.selectedReceiver = null;
    },
    setReceivers: (state, action: PayloadAction<any[]>) => {
      state.receivers = action.payload ?? [];
    },
    // Опционально: ручная установка страницы (если нужно)
    setInterPaymentsPage: (state, action: PayloadAction<number>) => {
      state.interPayments.current_page = action.payload ?? 1;
    },
    clearInterPaymentForEdit: (state) => {
      state.interPaymentForEdit = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // nationalities
      .addCase(fetchNationalities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNationalities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nationalities = action.payload ?? [];
      })
      .addCase(fetchNationalities.rejected, (state) => {
        state.isLoading = false;
      })
      // countries
      .addCase(fetchCountries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries = action.payload ?? [];
      })
      .addCase(fetchCountries.rejected, (state) => {
        state.isLoading = false;
      })
      // receivers
      .addCase(fetchReceivers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchReceivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.receivers = action.payload ?? [];
      })
      .addCase(fetchReceivers.rejected, (state) => {
        state.isLoading = false;
      })
      // inter payments (с пагинацией)
      .addCase(fetchInterPayments.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchInterPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        // ✅ НОВЫЙ формат: { list, current_page, last_page, per_page, total }
        if (payload?.list && Array.isArray(payload?.list)) {
          state.interPayments = {
            list: payload?.list ?? [],
            current_page: Number(payload?.current_page ?? 1),
            last_page: Number(payload?.last_page ?? 1),
            per_page: Number(payload?.per_page ?? payload?.list?.length ?? 12),
            total: Number(payload?.total ?? payload?.list?.length ?? 0),
          };
          return;
        }
        // 🧰 ФОЛБЭК: пришёл СТАРЫЙ формат (просто массив)
        if (Array.isArray(payload)) {
          state.interPayments = {
            list: payload ?? [],
            current_page: 1,
            last_page: 1,
            per_page: payload?.length ?? 12,
            total: payload?.length ?? 0,
          };
          return;
        }
        // На всякий случай — пустой
        state.interPayments = {
          list: [],
          current_page: 1,
          last_page: 1,
          per_page: 12,
          total: 0,
        };
      })
      .addCase(fetchInterPayments.rejected, (state) => {
        state.isLoading = false;
      })
      // inter payment for edit
      .addCase(fetchInterPaymentForEdit.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchInterPaymentForEdit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interPaymentForEdit = action.payload ?? null;
        console.log("📥 Fetched edit data in slice:", action.payload); // ← Добавь (в fulfilled)
      })
      .addCase(fetchInterPaymentForEdit.rejected, (state) => {
        state.isLoading = false;
        state.interPaymentForEdit = null;
      })
      // update inter payment
      .addCase(updateInterPayment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateInterPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data;
        const index = state.interPayments.list.findIndex(
          (item) => item.id === updated.id,
        );
        if (index !== -1) {
          state.interPayments.list[index] = updated;
        }
      })
      .addCase(updateInterPayment.rejected, (state) => {
        state.isLoading = false;
      });
  },
});
export const {
  setSelectedReceiver,
  clearSelectedReceiver,
  setReceivers,
  setInterPaymentsPage,
  clearInterPaymentForEdit,
} = receiverSlice.actions;
export default receiverSlice.reducer;
export const selectInterPayments = (s: any) =>
  s?.receiver?.interPayments?.list ?? [];
export const selectInterPaymentsMeta = (s: any) =>
  s?.receiver?.interPayments ?? initialState.interPayments;
export const selectInterPaymentsPage0 = (s: any) =>
  Math.max(0, (s?.receiver?.interPayments?.current_page ?? 1) - 1);
export const selectInterPaymentsTotal = (s: any) =>
  s?.receiver?.interPayments?.total ?? 0;
export const selectInterPaymentsPerPage = (s: any) =>
  s?.receiver?.interPayments?.per_page ?? 12;
export const selectInterPaymentForEdit = (s: any) =>
  s?.receiver?.interPaymentForEdit ?? null;
